/* eslint-disable camelcase */

import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'

import unionBy from 'lodash/unionBy'

import * as Yup from 'yup'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['account/createAccount'],
  isRolesLoading: loading['account/getUserRoles'],
  isCustodiansLoading: loading['account/getCustodians'],
})

const inputConfig = {
  inputBaseProps: {
    labelSuffix: '',
    justifyContent: 'stretch',
    labelProps: {
      sx: {
        color: theme => theme.palette.btn_positive.main,
        width: 100,
        justifyContent: 'flex-end',
      },
    },
    childrenSx: {
      flex: 1,
      '& .Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: theme => `2px solid ${theme.palette.btn_positive.main} !important`,
        },
      },
    },
  },
  color: 'btn_positive',
}

const CreateAccountDialog = ({
  dispatch,
  isLoading,
  isRolesLoading,
  isCustodiansLoading,
  onClose,
  onComplete,
  ...props
}) => {
  const rolePageRef = useRef(0)
  const custodianPageRef = useRef(0)
  const [rolesResults, setRolesResults] = useState({})
  const [custodiansResults, setCustodiansResults] = useState({})
  const form = useRef()
  const { t } = useTranslation()

  const { data: rolesResultsData = [], paging: rolesResultsPaging } = rolesResults
  const { data: custodiansResultsData = [], paging: custodiansResultsPaging } = custodiansResults

  const companyId = useSelector(state => state.auth.account?.companyId)

  const handleClose = () => {
    const { reset } = form.current
    reset?.()
    onClose?.()
  }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current

    formSubmit?.(values => {
      const { custodianId, email, phone, roleId, status, employeeId } = values

      const role = rolesResultsData.find(r => r.value.toString() === roleId)
      const custodian = custodiansResultsData.find(c => c.value.toString() === custodianId)

      dispatch({
        type: 'account/createAccount',
        payload: {
          email,
          phone,
          roleId,
          custodianId,
          roleName: role.label,
          custodian: custodian.label,
          status,
          employeeId,
        },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: { message: t('messages.account_created'), severity: 'success' },
          })
          onClose?.()
          onComplete?.()
        },
      })
    })?.()
  }

  const getRoles = params => {
    const { page = 0 } = params || {}
    if (!isRolesLoading) {
      rolePageRef.current = page

      dispatch({
        type: 'account/getUserRoles',
        payload: { pageIndex: rolePageRef.current, pageSize: 10 },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ value: d.id, label: d.roleName }))

          setRolesResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
            paging: responsePaging,
          }))
        },
      })
    }
  }

  const getCustodians = params => {
    const { page = 0 } = params || {}
    if (!isCustodiansLoading) {
      custodianPageRef.current = page

      dispatch({
        type: 'account/getCustodians',
        payload: { pageIndex: custodianPageRef.current, pageSize: 10, action: 'create' },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ value: d.id, label: d.accountName }))
          setCustodiansResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
            paging: responsePaging,
          }))
        },
      })
    }
  }

  useEffect(() => {
    if (props.open) {
      getRoles()
      getCustodians()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open])

  return (
    <Dialog {...props} maxWidth={'md'}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme => theme.palette.primary.main,
        }}
      >
        {t('general.add_account')}
      </DialogTitle>
      <DialogContent sx={{ width: 650, pr: 8 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 3 }}
          formConfig={{ mode: 'submit' }}
          formItems={[
            {
              label: t('general.account_type'),
              name: 'roleId',
              type: 'select',
              validator: Yup.string().required(t('validation.account_type_required')),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
                options: rolesResultsData,
                onLoadMore: () => {
                  if (rolesResultsPaging.hasNext) {
                    getRoles({ page: (rolePageRef.current += 1) })
                  }
                },
              },
            },
            {
              label: t('general.account_manager'),
              name: 'custodianId',
              type: 'select',
              validator: Yup.string().required(t('validation.account_manager_required')),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
                options: custodiansResultsData,
                onLoadMore: () => {
                  if (custodiansResultsPaging.hasNext) {
                    getCustodians({ page: (custodianPageRef.current += 1) })
                  }
                },
              },
            },
            {
              label: t('general.phone'),
              name: 'phone',
              validator: Yup.string()
                .required(t('validation.phone_required'))
                .matches(/^(09\d{8})?$/, t('validation.phone_ten_digits_start_zero_nine'))
                .test('check-starting-digits', 'Phone number must start with "09"', value => {
                  if (!value) return true
                  return value.startsWith('09')
                })
                .test('check-number-length', 'Phone number must have exactly 10 digits', value => {
                  if (!value) return true
                  return value.replace(/[^0-9]/g, '').length === 10
                }),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.email'),
              name: 'email',
              validator: Yup.string()
                .required(t('validation.email_required'))
                .email(t('validation.email_format')),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
              },
            },
            companyId === 1
              ? {
                  label: t('general.employeeId'),
                  name: 'employeeId',
                  validator: Yup.string()
                    .required(t('validation.employeeId_required'))
                    .matches(/^\d{7}$/, t('validation.employeeId_7_digits')),
                  defaultValue: '',
                  inputConfig: {
                    ...inputConfig,
                    inputProps: {
                      maxLength: 7,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    },
                  },
                }
              : null,
            {
              label: t('general.status'),
              name: 'status',
              validator: Yup.string().required(t('validation.new_password_required')),
              defaultValue: 'active',
              type: 'radio',
              inputConfig: {
                ...inputConfig,
                options: [
                  { value: 'active', label: t('status.ACTIVE') },
                  { value: 'inactive', label: t('status.INACTIVE') },
                ],
              },
            },
          ].filter(Boolean)}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>
      <DialogActions sx={{ alignSelf: 'center' }}>
        <Button
          sx={{ minWidth: 150, marginRight: '30px' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={handleClose}
        >
          {t('general.cancel')}
        </Button>
        <Button
          sx={{ minWidth: 150 }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(CreateAccountDialog)
